import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { portal, fetchProfilePhotoUrl, PortalContextData, BrandTheme } from '../lib/portal';
import { applyBrandTheme, rememberBrand } from '../lib/brand';

// Re-exported for existing importers; the implementation now lives in lib/brand
// so the pre-auth screens can re-skin themselves with the same logic.
export { applyBrandTheme };

/**
 * Program/brand/season-scoped resolution (Asana 1216271239389992): the tuple
 * is resolved ONCE per session from the BFF's /me/context and drives every
 * downstream scoped fetch — brand theming and program config here, workflow
 * steps via ctx.applicationId, season check-ins via ctx.season.id. When the
 * tuple cannot be resolved the failure is SURFACED (error / ctx.unresolved);
 * nothing ever falls back to a hardcoded or default program, brand or season.
 */
interface PortalContextState {
  ctx?: PortalContextData;
  theme?: BrandTheme;
  programConfig?: any;
  loading: boolean;
  error?: string;
  reload: () => void;
}

const Ctx = createContext<PortalContextState>({ loading: true, reload: () => {} });
export const usePortalContext = () => useContext(Ctx);

let lastPhotoObjectUrl: string | null = null;

export function PortalContextProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<PortalContextState, 'reload'>>({ loading: true });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: undefined }));
    (async () => {
      const ctx = await portal.context();
      // Downstream reference fetches keyed by the resolved ids. A theming or
      // config failure degrades to the neutral design system — never to
      // another brand's or program's data.
      const [theme, programConfig, photoUrl] = await Promise.all([
        ctx.brand?.id ? portal.brandTheme(ctx.brand.id).catch(() => undefined) : undefined,
        ctx.program?.id ? portal.programConfig(ctx.program.id).catch(() => undefined) : undefined,
        // Probe the BFF photo stream unconditionally: Globus's
        // /api/portal/applicant/profile payload carries NO image field
        // (verified on dev 2026-07-07), so ctx.profileImageUrl can't be
        // trusted as the existence signal. A 404 costs one tiny request and
        // resolves null → initials; a hit doubles as the avatar bytes,
        // swapped in as a blob object URL so plain <img src> works.
        fetchProfilePhotoUrl().catch(() => null),
      ]);
      // One live object URL per session — reload() (e.g. after a photo
      // upload) replaces it, so revoke the stale blob before swapping.
      if (lastPhotoObjectUrl) URL.revokeObjectURL(lastPhotoObjectUrl);
      lastPhotoObjectUrl = photoUrl;
      ctx.profileImageUrl = photoUrl;
      return { ctx, theme, programConfig };
    })()
      .then((next) => {
        if (!alive) return;
        applyBrandTheme(next.theme);
        // Cache the resolved brand so the pre-auth screens (sign-in,
        // forgot-password, onboarding) can wear it on this device next time.
        rememberBrand(next.ctx.brand, next.theme);
        setState({ ...next, loading: false });
      })
      .catch((e) => alive && setState({ loading: false, error: (e as Error).message }));
    return () => {
      alive = false;
    };
  }, [tick]);

  return <Ctx.Provider value={{ ...state, reload }}>{children}</Ctx.Provider>;
}
